import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, MessageCircle, QrCode, X } from 'lucide-react';
import type {
  DeepLinkCampaign,
  DeepLinkCampaignAnalyticsDetail,
} from '../../../models/MasterAdmin/deepLink';
import { DEEP_LINK_TARGET_APP_OPTIONS } from '../../../models/MasterAdmin/deepLink';
import {
  fetchDeepLinkCampaignAnalyticsDetail,
  fetchDeepLinkCampaignQr,
} from '../../../services/MasterAdmin/deepLinkService';
import CopyLinkButton from './CopyLinkButton';
import {
  dlBtnSecondaryClass,
  dlCardClass,
  dlModalHeaderClass,
  dlModalOverlayClass,
  dlMutedTextClass,
} from './deepLinkUi';

type Props = {
  open: boolean;
  campaign: DeepLinkCampaign | null;
  onClose: () => void;
};

function eventTone(eventType: string) {
  if (eventType === 'NAVIGATION_SUCCESS') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200';
  }
  if (eventType === 'NAVIGATION_MISMATCH' || eventType === 'NAVIGATION_ERROR') {
    return 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200';
  }
  return 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200';
}

const DeepLinkCampaignViewModal: React.FC<Props> = ({ open, campaign, onClose }) => {
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [detail, setDetail] = useState<DeepLinkCampaignAnalyticsDetail | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => {
    if (!open || !campaign?.id) {
      setQrUrl(null);
      setDetail(null);
      return;
    }
    void fetchDeepLinkCampaignQr(campaign.id)
      .then((data) => setQrUrl(data.qrUrl))
      .catch(() => setQrUrl(null));

    setLoadingAnalytics(true);
    void fetchDeepLinkCampaignAnalyticsDetail(campaign.id)
      .then((data) => setDetail(data))
      .catch(() => setDetail(null))
      .finally(() => setLoadingAnalytics(false));
  }, [open, campaign?.id]);

  if (!open || !campaign) return null;

  const summary = detail?.summary;
  const errors =
    detail?.recentNavigationEvents?.filter(
      (e) => e.eventType === 'NAVIGATION_MISMATCH' || e.eventType === 'NAVIGATION_ERROR'
    ) || [];
  const successes =
    detail?.recentNavigationEvents?.filter((e) => e.eventType === 'NAVIGATION_SUCCESS') || [];

  return (
    <div className={dlModalOverlayClass}>
      <div className="relative flex max-h-[92vh] w-full max-w-[96vw] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 lg:max-w-6xl">
        <div className={dlModalHeaderClass}>
          <h2 className="truncate pr-4 text-lg font-semibold text-gray-900 dark:text-white">
            {campaign.name}
          </h2>
          <button type="button" onClick={onClose} className="shrink-0 text-gray-500 dark:text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[minmax(280px,0.9fr)_minmax(420px,1.2fr)] lg:divide-x lg:divide-gray-200 dark:lg:divide-gray-700">
          {/* Left: campaign details + links */}
          <div className="space-y-4 overflow-y-auto p-4 text-sm sm:p-6">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className={dlMutedTextClass}>Route</p>
                <p className="font-medium text-gray-900 dark:text-white">{campaign.routeSlug}</p>
              </div>
              <div>
                <p className={dlMutedTextClass}>Status</p>
                <p className="font-medium text-gray-900 dark:text-white">{campaign.status}</p>
              </div>
              <div>
                <p className={dlMutedTextClass}>Target app</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {DEEP_LINK_TARGET_APP_OPTIONS.find(
                    (option) => option.value === (campaign.targetAppKey || 'user')
                  )?.label || 'Vedika.Health (User App)'}
                </p>
              </div>
              <div>
                <p className={dlMutedTextClass}>Platforms</p>
                <p className="text-gray-800 dark:text-gray-200">
                  {campaign.targetPlatforms?.join(', ')}
                </p>
              </div>
              <div>
                <p className={dlMutedTextClass}>Login required</p>
                <p className="text-gray-800 dark:text-gray-200">
                  {campaign.loginRequired ? 'Yes' : 'No'}
                </p>
              </div>
            </div>

            <div className={`${dlCardClass} p-3`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium text-gray-900 dark:text-white">Short link</p>
                <CopyLinkButton text={campaign.shortUrl} label="Copy link" />
              </div>
              <p className="break-all text-gray-700 dark:text-gray-300">{campaign.shortUrl}</p>
            </div>

            <div className={`${dlCardClass} p-3`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium text-gray-900 dark:text-white">Full deep link</p>
                <CopyLinkButton text={campaign.fullUrl} label="Copy link" />
              </div>
              <p className="break-all text-gray-700 dark:text-gray-300">{campaign.fullUrl}</p>
            </div>

            <div className={`${dlCardClass} p-3`}>
              <div className="mb-2 flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                <MessageCircle className="h-4 w-4" />
                WhatsApp usage
              </div>
              {campaign.whatsappUsage?.used ? (
                <div className="space-y-2 text-xs text-gray-700 dark:text-gray-300">
                  <p className="rounded-md bg-emerald-50 px-2 py-1 font-medium text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                    Used in {campaign.whatsappUsage.total} WhatsApp place(s)
                  </p>
                  {campaign.whatsappUsage.notifications.map((n) => (
                    <p key={n.eventKey}>
                      Notification: {n.label}
                      {n.templateName ? ` · ${n.templateName}` : ''}
                      {n.whatsappEnabled ? ' · enabled' : ' · disabled'}
                    </p>
                  ))}
                  {campaign.whatsappUsage.templates.map((t) => (
                    <p key={t.id}>Template: {t.displayName || t.name}</p>
                  ))}
                  {campaign.whatsappUsage.campaigns.map((c) => (
                    <p key={c.id}>
                      Campaign: {c.name}
                      {c.templateName ? ` · ${c.templateName}` : ''}
                    </p>
                  ))}
                </div>
              ) : (
                <p className={dlMutedTextClass}>Not used in any WhatsApp template or notification.</p>
              )}
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={onClose} className={dlBtnSecondaryClass}>
                Close
              </button>
            </div>
          </div>

          {/* Right: QR + Link analysis below */}
          <div className="space-y-4 overflow-y-auto border-t border-gray-200 p-4 sm:p-6 lg:border-t-0">
            <div className={`${dlCardClass} p-4 text-center`}>
              <div className="mb-3 inline-flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                <QrCode className="h-4 w-4" /> QR code
              </div>
              {qrUrl ? (
                <img
                  src={qrUrl}
                  alt="Campaign QR"
                  className="mx-auto h-36 w-36 rounded-lg sm:h-44 sm:w-44"
                />
              ) : (
                <p className={dlMutedTextClass}>QR unavailable</p>
              )}
            </div>

            <div className={`${dlCardClass} p-4`}>
              <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">Link analysis</h3>
              {loadingAnalytics ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading analytics…
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[
                      ['Clicks', summary?.totalClicks ?? campaign.totalClicks ?? 0],
                      ['Web opens', summary?.webOpens ?? 0],
                      ['App opens', summary?.appOpens ?? summary?.appClicks ?? 0],
                      ['Installs', summary?.installs ?? campaign.totalInstalls ?? 0],
                      ['Logins', summary?.logins ?? campaign.totalLogins ?? 0],
                      ['Nav OK', summary?.navigationSuccesses ?? 0],
                      ['Nav mismatch', summary?.navigationMismatches ?? 0],
                      ['Nav errors', summary?.navigationErrors ?? 0],
                    ].map(([label, value]) => (
                      <div
                        key={String(label)}
                        className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/80"
                      >
                        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    <p className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      Navigation errors / mismatches
                    </p>
                    {errors.length ? (
                      <div className="max-h-40 space-y-2 overflow-y-auto pr-1">
                        {errors.map((event) => (
                          <div
                            key={event.id}
                            className={`rounded-lg border p-3 text-xs ${eventTone(event.eventType)}`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-semibold">{event.eventType}</span>
                              <span>
                                {event.createdAt
                                  ? new Date(event.createdAt).toLocaleString()
                                  : '—'}
                              </span>
                            </div>
                            <p className="mt-1">
                              Intended: <code className="break-all">{event.intendedPath || '—'}</code>
                            </p>
                            <p className="mt-1">
                              Actual: <code className="break-all">{event.actualPath || '—'}</code>
                            </p>
                            {event.reason ? (
                              <p className="mt-1">Reason: {event.reason}</p>
                            ) : null}
                            {event.platform ? (
                              <p className="mt-1">Platform: {event.platform}</p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        No navigation mismatches or errors recorded for this link yet.
                      </p>
                    )}
                  </div>

                  {successes.length ? (
                    <div className="mt-4">
                      <p className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        Recent successful navigations
                      </p>
                      <div className="max-h-32 space-y-2 overflow-y-auto pr-1">
                        {successes.slice(0, 5).map((event) => (
                          <div
                            key={event.id}
                            className={`rounded-lg border p-3 text-xs ${eventTone(event.eventType)}`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-semibold">Reached intended screen</span>
                              <span>
                                {event.createdAt
                                  ? new Date(event.createdAt).toLocaleString()
                                  : '—'}
                              </span>
                            </div>
                            <p className="mt-1">
                              Path:{' '}
                              <code className="break-all">
                                {event.actualPath || event.intendedPath || '—'}
                              </code>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {detail?.recentClicks?.length ? (
                    <div className="mt-4">
                      <p className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        Recent opens
                      </p>
                      <div className="max-h-28 space-y-1 overflow-y-auto pr-1">
                        {detail.recentClicks.slice(0, 8).map((click) => (
                          <div
                            key={click.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-100 px-2 py-1.5 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-300"
                          >
                            <span>
                              {click.platform || 'UNKNOWN'} · {click.resolvedAction || '—'}
                            </span>
                            <span>
                              {click.createdAt
                                ? new Date(click.createdAt).toLocaleString()
                                : '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeepLinkCampaignViewModal;
